import { useEffect, useState } from "react";

import { fetchTransactions } from "../../utils/fetchTxn";
import { getFilteredTransactions } from "../../utils/filterUtils";
import { calculateHoldings } from "../../utils/Holding";
import { calculateStatistics } from "../../utils/Stat";
import { fetchWithdrawals } from "../utils/fetchWithdrawals";
import { usePartners } from "../../../hooks/Entry/usePartners";
import { useCallback } from "react";

export const usePartnership = (userId, showToast, options = {}) => {
    const [transactions, setTransactions] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [stats, setStats] = useState(null);

    // ✅ Get partners from hook (single source of truth)
    // ✅ Get partners from hook (single source of truth)
    const { partners: dbPartners } = usePartners(
        userId,
        showToast,
        {
            // ✅ INSTANT PARTNER UPDATE
            onPartnerUpdated: ({
                partnerId,
                name,
                myShare
            }) => {

                // 🔥 update transactions instantly
                setTransactions(prev =>
                    prev.map(tx =>
                        tx.partnerId === partnerId
                            ? {
                                ...tx,
                                partner: name,
                                myShare,
                                partnerShare: 100 - myShare
                            }
                            : tx
                    )
                );

                // 🔥 update selected partner instantly
                setSelectedPartner(prev =>
                    prev.id === partnerId
                        ? {
                            ...prev,
                            name,
                            myShare,
                            partnerShare: 100 - myShare
                        }
                        : prev
                );
            },

            // ✅ INSTANT PARTNER DELETE
            onPartnerDeleted: (partnerId) => {

                // 🔥 remove transactions instantly
                setTransactions(prev =>
                    prev.filter(
                        tx => tx.partnerId !== partnerId
                    )
                );

                // 🔥 reset selected partner if deleted
                setSelectedPartner(prev =>
                    prev.id === partnerId
                        ? {
                            id: "all",
                            name: "All Partners",
                            myShare: 100,
                            partnerShare: 0
                        }
                        : prev
                );
            },

            // ✅ INSTANT PARTNER ADD
            onPartnerAdded: (newPartner) => {

                // optional:
                // auto select newly added partner

                setSelectedPartner({
                    id: newPartner.id,
                    name: newPartner.name,
                    myShare: newPartner.myShare,
                    partnerShare:
                        100 - newPartner.myShare
                });
            }
        }
    );
    // ✅ Add "All Partners"
    const partners = [
        { id: "all", name: "All Partners", myShare: 100, partnerShare: 0 },
        ...dbPartners
    ];

    // ✅ Selected partner (always full object)
    const [selectedPartner, setSelectedPartner] = useState({
        id: "all",
        name: "All Partners",
        myShare: 100,
        partnerShare: 0
    });
    // 🔹 Fetch transactions
    useEffect(() => {
        if (userId) {
            fetchTransactions(
                userId,
                null,
                {
                    protectedUserId: userId
                }
            ).then(setTransactions);
        }
    }, [userId]);

    // 🔹 Fetch withdrawals
    useEffect(() => {
        if (userId) {
            fetchWithdrawals(userId, setWithdrawals);
        }
    }, [userId]);

    // ✅ Sync selectedPartner when partners load/update
    useEffect(() => {
        if (selectedPartner.id === "all") return;

        const fullPartner = partners.find(
            p => p.id === selectedPartner.id
        );

        if (fullPartner) {
            setSelectedPartner(fullPartner);
        }
    }, [dbPartners]);

    // 🔹 Core calculation
    const calculate = useCallback(() => {
        let filtered = getFilteredTransactions({
            transactions,
            filter: "all",
            selectedScrip: "all",
            selectedPartner
        });

        // ✅ Date filter
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);

            filtered = filtered.filter(tx => {
                const d = new Date(tx.date);
                return d >= start && d <= end;
            });
        }

        // ✅ Holdings
        const holdings = calculateHoldings(filtered, selectedPartner);

        // ✅ Stats
        const result = calculateStatistics({
            transactions: filtered,
            selectedScrip: "all",
            selectedPartner,
            holdings
        });

        // ✅ Withdrawals
        const partnerWithdrawals = withdrawals.filter(w =>
            selectedPartner.id === "all"
                ? true
                : w.partnerId === selectedPartner.id
        );

        const totalWithdrawn = partnerWithdrawals.reduce(
            (sum, w) => sum + Number(w.amount),
            0
        );

        const availableBalance =
            (Number(result.netProfitLoss) - totalWithdrawn).toFixed(2);

        setStats({
            ...result,
            totalWithdrawn,
            availableBalance
        });
    }, [
        transactions,
        withdrawals,
        selectedPartner,
        startDate,
        endDate
    ]);

    // 🔁 Auto calculate
    useEffect(() => {
        calculate();
    }, [calculate]);

    return {
        transactions,
        setTransactions,
        withdrawals,
        setWithdrawals,
        partners,
        selectedPartner,
        setSelectedPartner,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        stats,
        calculate
    };
};